<template>
  <div class="complete-profile-container">
    <div class="complete-profile-content">
      <h1>Welkom bij het Inspeqt-portaal</h1>
      <p class="intro">Vul uw gegevens aan om verder te gaan. Dit hoeft maar één keer.</p>

      <div v-if="loading" class="loading">Gegevens laden...</div>
      <form v-else @submit.prevent="handleSubmit" class="profile-form">
        <div class="form-section">
          <h2>Bedrijfsgegevens</h2>
          <div class="form-group">
            <InputField :label="isInstallateur && !isOpdrachtgever ? 'Naam contactpersoon' : 'Bedrijfsnaam'" v-model="form.naam" required />
          </div>
          <div v-if="isOpdrachtgever" class="form-group">
            <InputField label="Contactpersoon" v-model="form.contactnaam" required />
          </div>
          <div v-if="isInstallateur" class="form-group">
            <InputField label="Bedrijfsnaam (installatiebedrijf)" v-model="form.bedrijfsnaam" />
          </div>
          <div v-if="isInstallateur" class="form-group">
            <InputField label="KvK-nummer" v-model="form.kvk" />
          </div>
        </div>

        <div class="form-section">
          <h2>Adresgegevens</h2>
          <div class="form-row">
            <div class="form-group">
              <InputField label="Straat" v-model="form.straat" required />
            </div>
            <div class="form-group small">
              <InputField label="Huisnummer" v-model="form.huisnr" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <InputField label="Postcode" v-model="form.postcode" required />
            </div>
            <div class="form-group">
              <InputField label="Plaats" v-model="form.plaats" required />
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2>Contactgegevens</h2>
          <div class="form-group">
            <InputField label="Telefoonnummer" v-model="form.telnr" type="tel" required />
          </div>
        </div>

        <div class="form-actions">
          <Button
            type="submit"
            label="Opslaan en verdergaan"
            icon="pi pi-arrow-right"
            iconPos="right"
            :loading="saving"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { supabase } from '../lib/supabase'
  import { useAuthStore } from '../stores/auth'
  import { useToast } from '../composables/useToast'
  import {
    clearStoredPostLoginRedirect,
    getStoredPostLoginRedirect,
    sanitizeRedirectTarget,
  } from '../utils/authNavigation'
  import InputField from '../components/InputField.vue'
  import Button from 'primevue/button'

  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const toast = useToast()

  const loading = ref(true)
  const saving = ref(false)
  const form = ref({
    naam: '',
    contactnaam: '',
    bedrijfsnaam: '',
    kvk: '',
    straat: '',
    huisnr: '',
    postcode: '',
    plaats: '',
    telnr: '',
  })

  // A user can be both opdrachtgever and installateur; show the union of fields.
  const isOpdrachtgever = computed(() => !!authStore.profileMeta?.isOpdrachtgever)
  const isInstallateur = computed(() => !!authStore.profileMeta?.isInstallateur)

  const loadProfile = async () => {
    loading.value = true
    try {
      const meta = await authStore.ensureProfileMeta()
      const userId = authStore.user?.id
      if (!meta || !userId) return

      if (meta.isOpdrachtgever) {
        const { data } = await supabase
          .from('opdrachtgevers')
          .select('naam, contactnaam, telnr, straat, huisnr, postcode, plaats')
          .eq('id', userId)
          .maybeSingle()
        if (data) {
          form.value.naam = data.naam || ''
          form.value.contactnaam = data.contactnaam || ''
          form.value.telnr = data.telnr || ''
          form.value.straat = data.straat || ''
          form.value.huisnr = data.huisnr || ''
          form.value.postcode = data.postcode || ''
          form.value.plaats = data.plaats || ''
        }
      }

      if (meta.isInstallateur) {
        const { data } = await supabase
          .from('installateurs')
          .select('naam, bedrijfsnaam, kvk, telnr, straat, huisnr, postcode, plaats')
          .eq('id', userId)
          .maybeSingle()
        if (data) {
          form.value.naam = form.value.naam || data.naam || ''
          form.value.bedrijfsnaam = data.bedrijfsnaam || ''
          form.value.kvk = data.kvk || ''
          form.value.telnr = form.value.telnr || data.telnr || ''
          form.value.straat = form.value.straat || data.straat || ''
          form.value.huisnr = form.value.huisnr || data.huisnr || ''
          form.value.postcode = form.value.postcode || data.postcode || ''
          form.value.plaats = form.value.plaats || data.plaats || ''
        }
      }

      // The invited account was pre-filled with the email local-part; don't show
      // that placeholder as if it were a real name.
      const emailLocalPart = (authStore.user?.email || '').split('@')[0]
      if (form.value.naam === emailLocalPart) {
        form.value.naam = ''
      }
    } catch (error) {
      console.error('Error loading profile for completion:', error)
      toast.error('Er is een fout opgetreden bij het laden van uw gegevens', error)
    } finally {
      loading.value = false
    }
  }

  const handleSubmit = async () => {
    saving.value = true
    try {
      const { error } = await supabase.rpc('complete_portal_profile', {
        p_profile: {
          ...form.value,
          display_name: form.value.contactnaam || form.value.naam,
        },
      })
      if (error) throw error

      authStore.markProfileCompleted()
      toast.success('Uw gegevens zijn opgeslagen')

      const redirectQuery = typeof route.query.redirect === 'string' ? route.query.redirect : null
      const target = sanitizeRedirectTarget(redirectQuery || getStoredPostLoginRedirect() || '/')
      clearStoredPostLoginRedirect()
      router.replace(target)
    } catch (error) {
      console.error('Error completing profile:', error)
      toast.error('Er is een fout opgetreden bij het opslaan van uw gegevens', error)
    } finally {
      saving.value = false
    }
  }

  onMounted(() => {
    loadProfile()
  })
</script>

<style scoped>
  /* Colours come from the PrimeVue preset (mytheme.ts), mirroring the login
     card, so the page follows the portal's light/dark mode. */
  .complete-profile-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
  }

  .complete-profile-content {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 4px;
    box-shadow: var(--shadow-sm);
    padding: 2rem;
    margin-top: 2rem;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--p-background-900);
  }

  .intro {
    color: var(--p-background-600);
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }

  .profile-form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin-bottom: 0.5rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group.small {
    max-width: 120px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .loading {
    text-align: center;
    padding: 2rem;
    color: var(--p-background-600);
  }
</style>
